import { Component, computed, effect, inject, input, model } from "@angular/core";
import { Router } from "@angular/router";
import { Lot } from "app/features/lot/models/lot.model";
import { LotFacadeService } from "../../../features/lot/services/store/lot-facade.service";
import { ChipsComponent } from "../../modules/chips/chips.component";
import { SharedModule } from "app/shared/shared.module";
import { NgClass } from "@angular/common";
import { getDlcStatus, DlcStatus } from "../palette-display/palette-display.utils";
import { format } from "date-fns";
import { PopoverModule } from "../../modules/popover/popover.module";
import { BigButtonComponent } from "../big-button/big-button.component";
import { DateLimiteDisplayComponent } from "../date-limite-display/date-limite-display.component";

@Component({
  selector: "vl-lot-card",
  standalone: true,
  imports: [ChipsComponent, SharedModule, NgClass, PopoverModule, BigButtonComponent, DateLimiteDisplayComponent],
  templateUrl: "./lot-card.component.html",
  styleUrls: ["./lot-card.component.scss"],
})
export class LotCardComponent {
  facade = inject(LotFacadeService);

  /** Dedup in-flight loadById requests across all lot-card instances */
  private static pendingLoads = new Map<string, Promise<any>>();

  lotId = input<string>();
  lotInput = input<Lot>();
  lot = model<Lot>();
  mode = input<"chips" | "text" | "detail">("chips");
  noAction = input<boolean>(false);
  isShipped = input<boolean>(false);
  dluoFormat = input<string>("dd/MM/yyyy");

  dlcStatus = computed<DlcStatus>(() => {
    const lot = this.lot();
    if (!lot?.dluo) return "ok";
    return getDlcStatus(lot.dluo, this.isShipped());
  });

  formattedDluo = computed(() => {
    const lot = this.lot();
    if (!lot?.dluo) return "";
    try {
      return format(new Date(lot.dluo), this.dluoFormat());
    } catch {
      return "";
    }
  });

  /**
   * R4 refonte DDM/DLC — label "DDM" ou "DLC" affiché à côté de la date.
   * Lit `lot.typeDateLimite` (snapshot figé). Default "DDM" pour rétro-compat.
   * Cf. `docs/plans/roadmap-ddm-prepa.md`.
   */
  dateLimiteLabel = computed(() => {
    return this.lot()?.typeDateLimite === "DLC" ? "DLC" : "DDM";
  });

  /** Full lot loaded from the store (has ofId, receptionsCommandes, etc.) */
  fullLot = computed(() => {
    const id = this.lot()?.id;
    if (!id) return null;
    return this.facade.getById(id)();
  });

  hasOf = computed(() => !!this.fullLot()?.ofId);
  hasCommande = computed(() => {
    const fl = this.fullLot();
    return fl?.receptionsCommandes?.length > 0;
  });

  constructor(private router: Router) {
    effect(
      () => {
        if (this.lotId && this.lotId()) {
          const id = this.lotId();
          const lot = this.facade.getById(id)();
          this.lot.set(lot);

          if (!lot && !LotCardComponent.pendingLoads.has(id)) {
            const promise = this.facade.loadByIdLight(id).finally(() => {
              LotCardComponent.pendingLoads.delete(id);
            });
            LotCardComponent.pendingLoads.set(id, promise);
          }
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.lotInput()) {
          this.lot.set(this.lotInput());
        }
      },
      { allowSignalWrites: true },
    );
  }

  onOpenPopover() {
    if (this.noAction()) return;
    const lot = this.lot();
    if (lot?.id && !this.fullLot()) {
      this.facade.loadById(lot.id);
    }
  }

  onGoLot() {
    const lot = this.lot();
    if (!lot?.id) return;
    this.router.navigate(["/lots/infos/"], {
      queryParams: { lotId: lot.id },
    });
  }

  onGoOf() {
    const fl = this.fullLot();
    if (!fl?.ofId) return;
    this.router.navigate(["/productions/suivi/ope"], {
      queryParams: { ofId: fl.ofId },
    });
  }

  onGoCommande() {
    const fl = this.fullLot();
    const commandeId = fl?.receptionsCommandes?.[0]?.commandeId;
    if (!commandeId) return;
    this.router.navigate([`/commandes/details/id/${commandeId}/commande`]);
  }

  onGoArticle() {
    const lot = this.fullLot() ?? this.lot();
    if (!lot?.articleId) return;
    this.router.navigateByUrl(`/specification/id/${lot.articleId}`);
  }
}
